import React from "react";

interface FeatureCellData {
  check: boolean;
  sub?: string;
}

interface FeatureRowData {
  feature: string;
  free: FeatureCellData | null;
  premium: FeatureCellData | null;
  enterprise: FeatureCellData | null;
}

interface FeatureGroupData {
  category: string;
  rows: FeatureRowData[];
}

const FEATURE_GROUPS: FeatureGroupData[] = [
  {
    category: "Single Image Watermark",
    rows: [
      {
        feature: "Image watermark",
        free: { check: true, sub: "1 (shares slot with sticker)" },
        premium: { check: true, sub: "Unlimited" },
        enterprise: { check: true, sub: "Unlimited" },
      },
      {
        feature: "Signature watermark",
        free: { check: true, sub: "1 only" },
        premium: { check: true, sub: "Unlimited" },
        enterprise: { check: true, sub: "Unlimited" },
      },
      {
        feature: "Text watermark",
        free: { check: true, sub: "1 only" },
        premium: { check: true, sub: "Unlimited" },
        enterprise: { check: true, sub: "Unlimited" },
      },
      {
        feature: "Sticker watermark",
        free: { check: true, sub: "1 (shares slot with image)" },
        premium: { check: true, sub: "Unlimited" },
        enterprise: { check: true, sub: "Unlimited" },
      },
    ],
  },
  {
    category: "Batch Watermark",
    rows: [
      {
        feature: "Image watermark",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Signature watermark",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Text watermark",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Sticker watermark",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Images processed per batch",
        free: { check: true, sub: "Up to 5 images" },
        premium: { check: true, sub: "Unlimited" },
        enterprise: { check: true, sub: "Unlimited" },
      },
      {
        feature: "Template selection for batch",
        free: { check: true, sub: "Last-created template only" },
        premium: { check: true, sub: "Any template" },
        enterprise: { check: true, sub: "Any template" },
      },
      {
        feature: "Insert more than 3 watermarks at once",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
    ],
  },
  {
    category: "Templates",
    rows: [
      {
        feature: "Use watermark templates",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Use all templates",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Use all recent logos",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Sync templates across platforms",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Sync templates on cloud",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
    ],
  },
  {
    category: "Editing & Export",
    rows: [
      {
        feature: "Access 300+ fonts",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Export files to other apps",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Download watermarks in high-res",
        free: { check: true },
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Maintain resolutions",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Insert Date/Time in autograph",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "Save autograph",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
    ],
  },
  {
    category: "Platform & Access",
    rows: [
      {
        feature: "Full access to Photo and Video apps under one subscription",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
    ],
  },
  {
    category: "Support & Extras",
    rows: [
      {
        feature: "No ads",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
      {
        feature: "No purchase for stickers",
        free: null,
        premium: { check: true },
        enterprise: { check: true },
      },
    ],
  },
];

const renderCell = (cell: FeatureCellData | null, tone: "free" | "premium") => {
  if (!cell || !cell.check) {
    return <span className="dash">&mdash;</span>;
  }
  return (
    <>
      <span className={`check ${tone}`}>&#10003;</span>
      {cell.sub && <span className="sub">{cell.sub}</span>}
    </>
  );
};

const PricingCompare: React.FC = () => {
  return (
    <section className="pp-compare">
      <div className="pp-compare__head">
        <h2 className="pp-compare__heading">Features comparison</h2>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th className="center">Free</th>
              <th className="center">Premium</th>
              <th className="center">Enterprise</th>
            </tr>
          </thead>
          <tbody>
            {FEATURE_GROUPS.map((group) => (
              <React.Fragment key={group.category}>
                <tr className="section-header">
                  <td colSpan={4}>{group.category}</td>
                </tr>
                {group.rows.map((row, idx) => (
                  <tr key={`${group.category}-${idx}`}>
                    <td className="feature-name">{row.feature}</td>
                    <td className="center">{renderCell(row.free, "free")}</td>
                    <td className="center">{renderCell(row.premium, "premium")}</td>
                    <td className="center">{renderCell(row.enterprise, "premium")}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PricingCompare;
